// Spellings that name one grocery, across the two languages the list is
// written in. `normalize.ts` folds this into `nameKey`, so an English recipe's
// "garlic" and the "Vitlök" already on the list share one key — and with it the
// recipe importer's merge, "what am I missing", the add bar's suggestions and
// the household's category memory all agree that they are the same thing.
//
// Each group's first entry is the canonical spelling (English, by convention);
// the rest are the other spellings, in either language, single words or
// phrases. Written with diacritics as people type them — normalize folds and
// stems every entry the same way it treats a name, so plurals/definite forms
// ("ärtor", "räkorna") need no extra rows. Words that already land on one stem
// ("banan"/"banana", "äpple"/"apple", "peppar"/"pepper") need no entry either.
//
// Pure data, no imports: normalize.ts imports this, not the other way round.
// synonyms.test.ts pins that no spelling sits in two groups.
export const SYNONYMS: string[][] = [
  // ── vegetables ─────────────────────────────────────────────────────────────
  ['onion', 'lök'],
  ['yellow onion', 'gul lök'],
  ['red onion', 'rödlök', 'röd lök'],
  ['garlic', 'vitlök'],
  ['spring onion', 'scallion', 'salladslök'],
  ['leek', 'purjolök', 'purjo'],
  ['shallot', 'schalottenlök'],
  ['potato', 'potatis'],
  ['sweet potato', 'sötpotatis'],
  ['carrot', 'morot', 'morötter'],
  ['tomato', 'tomat'],
  ['tomato paste', 'tomato puree', 'tomatpuré'],
  ['cucumber', 'gurka'],
  ['bell pepper', 'capsicum', 'paprika'],
  ['chilli', 'chili', 'chile'],
  ['spinach', 'spenat'],
  ['lettuce', 'sallad', 'salladshuvud'],
  ['cabbage', 'vitkål'],
  ['red cabbage', 'rödkål'],
  ['cauliflower', 'blomkål'],
  ['kale', 'grönkål'],
  ['brussels sprout', 'brysselkål'],
  ['mushroom', 'champinjon', 'svamp'],
  ['avocado', 'avokado'],
  ['aubergine', 'eggplant'],
  ['zucchini', 'courgette'],
  ['celery', 'selleri'],
  ['corn', 'sweetcorn', 'majs'],
  ['pea', 'ärtor', 'ärta'],
  ['bean', 'bönor', 'böna'],
  ['green bean', 'haricots verts', 'gröna bönor'],
  ['chickpea', 'kikärtor', 'kikärta'],
  ['lentil', 'linser', 'lins'],
  ['beetroot', 'beet', 'rödbeta', 'rödbetor'],
  ['radish', 'rädisa'],
  ['asparagus', 'sparris'],
  ['fennel', 'fänkål'],
  ['pumpkin', 'pumpa'],
  ['ginger', 'ingefära'],
  // ── fruit ──────────────────────────────────────────────────────────────────
  ['lemon', 'citron'],
  ['orange', 'apelsin'],
  ['pear', 'päron'],
  ['grape', 'vindruva', 'druva', 'druvor'],
  ['strawberry', 'jordgubbe', 'jordgubbar'],
  ['raspberry', 'hallon'],
  ['blueberry', 'blåbär'],
  ['lingonberry', 'lingon'],
  ['cherry', 'körsbär'],
  ['peach', 'persika'],
  ['plum', 'plommon'],
  ['pineapple', 'ananas'],
  ['watermelon', 'vattenmelon'],
  // ── herbs & spices ─────────────────────────────────────────────────────────
  ['parsley', 'persilja'],
  ['coriander', 'cilantro', 'koriander'],
  ['basil', 'basilika'],
  ['thyme', 'timjan'],
  ['rosemary', 'rosmarin'],
  ['bay leaf', 'lagerblad'],
  ['mint', 'mynta'],
  ['chives', 'gräslök'],
  ['black pepper', 'svartpeppar'],
  ['white pepper', 'vitpeppar'],
  ['cinnamon', 'kanel'],
  ['cumin', 'spiskummin'],
  ['nutmeg', 'muskot', 'muskotnöt'],
  ['cardamom', 'kardemumma'],
  ['clove', 'kryddnejlika', 'nejlika'],
  ['vanilla', 'vanilj'],
  ['vanilla sugar', 'vaniljsocker'],
  ['turmeric', 'gurkmeja'],
  ['saffron', 'saffran'],
  ['allspice', 'kryddpeppar'],
  ['caraway', 'kummin'],
  ['stock cube', 'bouillon cube', 'buljongtärning'],
  // ── dairy & eggs ───────────────────────────────────────────────────────────
  ['milk', 'mjölk'],
  ['cream', 'grädde'],
  ['whipping cream', 'heavy cream', 'double cream', 'vispgrädde'],
  ['sour cream', 'gräddfil'],
  ['cooking cream', 'single cream', 'matlagningsgrädde'],
  ['butter', 'smör'],
  ['cheese', 'ost'],
  ['egg', 'ägg'],
  ['cottage cheese', 'keso'],
  ['cream cheese', 'färskost'],
  ['soured milk', 'buttermilk', 'filmjölk'],
  ['quark', 'kvarg'],
  // ── meat & fish ────────────────────────────────────────────────────────────
  ['chicken', 'kyckling'],
  ['chicken breast', 'chicken fillet', 'kycklingfilé'],
  ['chicken thigh', 'kycklinglårfilé', 'kycklinglår'],
  ['beef', 'nötkött'],
  // "minced" is a prep word and is stripped before keying, so "minced beef"
  // already keys to "beef"; the groups here are for the spellings that don't.
  ['ground beef', 'nötfärs'],
  ['ground meat', 'köttfärs', 'blandfärs'],
  ['ground pork', 'fläskfärs'],
  ['pork', 'fläsk', 'gris', 'griskött'],
  ['pork chop', 'fläskkotlett'],
  ['lamb', 'lamm'],
  ['sausage', 'korv'],
  ['ham', 'skinka'],
  ['salmon', 'lax'],
  ['cod', 'torsk'],
  ['tuna', 'tonfisk'],
  ['fish', 'fisk'],
  ['shrimp', 'prawn', 'räkor', 'räka'],
  ['mussel', 'mussla', 'musslor'],
  ['meatball', 'köttbulle', 'köttbullar'],
  // ── pantry ─────────────────────────────────────────────────────────────────
  ['flour', 'mjöl', 'vetemjöl'],
  ['sugar', 'socker'],
  ['icing sugar', 'powdered sugar', 'florsocker'],
  ['brown sugar', 'farinsocker'],
  ['oil', 'olja'],
  ['olive oil', 'olivolja'],
  ['rapeseed oil', 'canola oil', 'rapsolja'],
  ['vinegar', 'vinäger'],
  ['soy sauce', 'soja', 'sojasås'],
  ['stock', 'broth', 'buljong'],
  ['chicken stock', 'chicken broth', 'kycklingbuljong', 'hönsbuljong'],
  ['vegetable stock', 'vegetable broth', 'grönsaksbuljong'],
  ['beef stock', 'beef broth', 'köttbuljong'],
  ['rice', 'ris'],
  ['noodle', 'nudlar', 'nudel'],
  ['bread', 'bröd'],
  ['breadcrumbs', 'ströbröd'],
  ['oats', 'rolled oats', 'havregryn'],
  ['honey', 'honung'],
  ['mustard', 'senap'],
  ['baking powder', 'bakpulver'],
  ['baking soda', 'bicarbonate of soda', 'bikarbonat'],
  ['yeast', 'jäst'],
  ['coconut milk', 'kokosmjölk'],
  ['almond', 'mandel'],
  ['walnut', 'valnöt'],
  ['hazelnut', 'hasselnöt'],
  ['peanut', 'jordnöt'],
  ['peanut butter', 'jordnötssmör'],
  ['nut', 'nöt', 'nötter'],
  ['raisin', 'russin'],
  ['chocolate', 'choklad'],
  ['cocoa', 'kakao'],
  ['jam', 'sylt'],
  ['mayonnaise', 'mayo', 'majonnäs'],
  ['crispbread', 'knäckebröd'],
  ['tortilla', 'tortillabröd'],
  ['spaghetti', 'spagetti'],
  ['cornflour', 'cornstarch', 'majsstärkelse', 'maizena'],
  ['potato starch', 'potatismjöl'],
  ['syrup', 'sirap'],
  ['golden syrup', 'ljus sirap'],
  // ── drinks & frozen ────────────────────────────────────────────────────────
  ['water', 'vatten'],
  ['wine', 'vin'],
  ['white wine', 'vitt vin', 'vitvin'],
  ['red wine', 'rött vin', 'rödvin'],
  ['juice', 'saft'],
  ['coffee', 'kaffe'],
  ['tea', 'te'],
  ['beer', 'öl'],
  ['ice cream', 'glass'],
]
